import React from 'react'
import { Copy, MoreHorizontal, Edit2, Trash2, MessageSquare } from 'lucide-react'
import { Menu, MenuItem, MenuItems, MenuButton } from '@headlessui/react'
import { toast } from 'react-hot-toast'
import { CrmComment } from 'types/crm/comments'
import { ContactOwner } from 'types/crm/contacts'
import { getInitials } from 'utils/getInitials'
import { getExactFormatDate } from 'utils/helpers'
import TipTapEditor from 'components/common/RichTextEditor/RichTextEditor'

interface CrmReplyItemProps {
    reply: CrmComment
    rootCommentUuid: string
    currentUserId?: string
    mentionUsers?: ContactOwner[]

    // Resolved client-side by the parent (CrmCommentThread) via a commentUuid -> name map,
    // since the backend doesn't send parentAuthorName on the comment itself.
    parentAuthorName?: string
    isReplyingToRoot: boolean
    isHighlighted?: boolean

    onReplyClick: (reply: CrmComment) => void
    onDeleteClick: (reply: CrmComment) => void
    onJumpToParent: (parentCommentUuid: string) => void

    // Inline edit — controlled by the parent (CrmCommentThread) which owns allReplies
    isEditing: boolean
    editText: string
    onEditTextChange: (text: string) => void
    onStartEdit: (reply: CrmComment) => void
    onCancelEdit: () => void
    onSaveEdit: () => void
    isUpdating: boolean
}

const CrmReplyItem: React.FC<CrmReplyItemProps> = ({
    reply,
    rootCommentUuid,
    currentUserId,
    mentionUsers = [],
    parentAuthorName,
    isReplyingToRoot,
    isHighlighted = false,
    onReplyClick,
    onDeleteClick,
    onJumpToParent,
    isEditing,
    editText,
    onEditTextChange,
    onStartEdit,
    onCancelEdit,
    onSaveEdit,
    isUpdating,
}) => {
    const isOwner = !!(currentUserId && reply.createdByUuid === currentUserId)

    const handleCopyLink = () => {
        const hash = `#comment-${reply.commentUuid}_root-${rootCommentUuid}`
        navigator.clipboard
            .writeText(`${window.location.href.split('#')[0]}${hash}`)
            .then(() => toast.success('Link copied to clipboard'))
            .catch(() => toast.error('Failed to copy link'))
    }

    return (
        <div
            id={`comment-${reply.commentUuid}`}
            className={`flex gap-3 group min-w-0 rounded-lg transition-colors duration-500 ${
                isHighlighted ? 'bg-blue-500/10 ring-1 ring-blue-500/40 -m-2 p-2' : ''
            }`}
        >
            <div className="w-7 h-7 bg-primary-subtle rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                {getInitials(reply.createdByName || 'U')}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-neutral text-sm">
                            {reply.createdByName || 'You'}
                        </span>
                        <span className="text-neutral-medium text-xs">
                            {reply.createdAt ? getExactFormatDate(reply.createdAt) : 'just now'}
                        </span>
                        {reply.isEdited && (
                            <span className="text-[10px] bg-primary-subtle text-neutral-medium px-2 py-0.5 rounded-full">
                                Edited
                            </span>
                        )}
                        <button
                            onClick={handleCopyLink}
                            className="p-1 rounded-md text-neutral-medium hover:text-blue-400 transition-colors"
                            title="Copy link"
                        >
                            <Copy className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {isOwner && !isEditing && (
                        <Menu as="div" className="relative inline-block text-left flex-shrink-0">
                            <MenuButton className="p-1 rounded-md border border-blue-500/30 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors">
                                <MoreHorizontal className="w-4 h-4" />
                            </MenuButton>
                            <MenuItems anchor="bottom end" className="w-40 rounded-lg bg-primary-variant border border-primary-subtle shadow-lg z-20 overflow-hidden">
                                <div className="p-1">
                                    <MenuItem>
                                        {({ focus }) => (
                                            <button
                                                onClick={() => onStartEdit(reply)}
                                                className={`${focus ? 'bg-primary-subtle text-neutral' : 'text-neutral-medium'} flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm`}
                                            >
                                                <Edit2 className="w-4 h-4" /> Edit
                                            </button>
                                        )}
                                    </MenuItem>
                                    <MenuItem>
                                        {({ focus }) => (
                                            <button
                                                onClick={() => onDeleteClick(reply)}
                                                className={`${focus ? 'bg-red-500/10 text-red-500' : 'text-red-500/80'} flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm`}
                                            >
                                                <Trash2 className="w-4 h-4" /> Delete
                                            </button>
                                        )}
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </Menu>
                    )}
                </div>

                {!isReplyingToRoot && reply.parentCommentUuid && (
                    <button
                        type="button"
                        onClick={() => onJumpToParent(reply.parentCommentUuid as string)}
                        className="text-xs text-neutral-faded mt-0.5 hover:text-neutral-light transition-colors"
                    >
                        replying to{' '}
                        <span className="text-blue-400 font-medium hover:text-blue-300 hover:underline">
                            {parentAuthorName || 'a comment'}
                        </span>
                    </button>
                )}

                {isEditing ? (
                    <div className="mt-2 w-full border border-primary-subtle rounded-lg overflow-hidden bg-primary-variant min-w-0">
                        <TipTapEditor
                            initialContent={editText}
                            onChange={onEditTextChange}
                            placeholder="Edit reply..."
                            className={isUpdating ? 'opacity-50 pointer-events-none min-h-[50px]' : 'min-h-[50px]'}
                            mentionUsers={mentionUsers}
                            isComment
                        />
                        <div className="flex justify-end gap-2 p-2 bg-primary">
                            <button
                                onClick={onCancelEdit}
                                disabled={isUpdating}
                                className="px-3 py-1.5 text-xs font-medium text-neutral-medium hover:text-neutral hover:bg-primary-subtle rounded disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onSaveEdit}
                                disabled={isUpdating || !editText.trim()}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                            >
                                {isUpdating ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div
                            className="text-neutral-light text-sm leading-relaxed prose prose-sm prose-invert max-w-none mt-1 break-words overflow-x-auto [&_table]:w-full [&_table]:table-fixed [&_td]:break-words"
                            dangerouslySetInnerHTML={{ __html: reply.commentText }}
                        />
                        <button
                            onClick={() => onReplyClick(reply)}
                            className="mt-2 text-xs font-medium text-blue-400/80 hover:text-blue-400 flex items-center gap-1"
                        >
                            <MessageSquare className="w-3.5 h-3.5" /> Reply
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}

export default CrmReplyItem